import { FC, useMemo, MouseEvent } from 'react'
import { Stack, MenuItem, SelectChangeEvent } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { TreeSelectOption } from 'models'
import { StyledChip, StyledSelect } from './selectedItemsDisplay.styles'
import { DEFAULT_MENU_PROPS } from './selectedItemDisplay.configs'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { ExclamationCircleIcon } from '@heroicons/react/24/solid'
import theme from 'theme'

interface SelectedItemsDisplayProps {
  value: TreeSelectOption[]
  allOptions: TreeSelectOption[]
  parentType: string
  childType: string
  onChange: (newValue: TreeSelectOption[]) => void
  invalidItems?: Set<string>
}

const SelectedItemsDisplay: FC<SelectedItemsDisplayProps> = ({
  value,
  allOptions,
  parentType,
  childType,
  onChange,
  invalidItems
}) => {
  // Flatten tree to display items
  // If all CPIs in a category are selected, show as "Category: {name}" instead of individual CPIs
  const selectedItems = useMemo(() => {
    const allCategoriesSelected =
      value.length === allOptions.length &&
      value.every((selected) => {
        const original = allOptions.find((opt) => opt.value === selected.value)
        return original && selected.children?.length === original.children?.length
      })

    if (allCategoriesSelected) {
      return [{ value: 'all', label: 'All categories', type: '' }]
    }

    const items: Array<{ value: string; label: string; type: string }> = []
    value.forEach((parent) => {
      const hasChildren = parent.children && parent.children.length > 0
      const originalParent = allOptions.find((opt) => opt.value === parent.value)
      const allCpisSelected =
        originalParent && hasChildren && parent.children?.length === originalParent.children?.length

      if (allCpisSelected) {
        items.push({
          value: parent.value,
          label: parent.label,
          type: parentType
        })
        return
      }

      if (!hasChildren) {
        items.push({
          value: parent.value,
          label: parent.label,
          type: parentType
        })
      }

      parent.children?.forEach((child) => {
        items.push({
          value: child.value,
          label: child.label,
          type: childType
        })
      })
    })
    return items
  }, [value, allOptions, parentType, childType])

  // Split items: 1-2 in first row, 3rd item as chip, 4+ in select
  const firstRowItems = selectedItems.length >= 3 ? selectedItems.slice(0, 2) : selectedItems
  const secondRowChipItem = selectedItems.length >= 3 ? selectedItems[2] : null
  const selectItems = selectedItems.length >= 4 ? selectedItems.slice(3) : []

  // Helper: Filter out an item from tree
  const removeFromTree = (itemValue: string): TreeSelectOption[] => {
    const mappedParents = value.map((parent) => {
      if (parent.value === itemValue) {
        return null
      }
      const remainingChildren = parent.children?.filter((child) => child.value !== itemValue)
      const hasRemainingChildren = remainingChildren?.length
      return hasRemainingChildren ? { ...parent, children: remainingChildren } : null
    })
    const filteredResult = mappedParents.filter((item): item is NonNullable<typeof item> => item !== null)
    return filteredResult
  }

  const handleRemoveItem = (itemValue: string) => {
    if (itemValue === 'all') {
      onChange([])
      return
    }
    const newTree = removeFromTree(itemValue)
    onChange(newTree)
  }

  const handleRemainingItemRemove = (event: SelectChangeEvent<unknown>) => {
    const remainingValues = event.target.value as string[]
    const firstRowValues = firstRowItems.map((item) => item.value)
    const thirdItemValue = secondRowChipItem ? [secondRowChipItem.value] : []
    const keptValues = new Set([...firstRowValues, ...thirdItemValue, ...remainingValues])

    const mappedParents = value.map((parent) => {
      const keptChildren = parent.children?.filter((child) => keptValues.has(child.value))
      const shouldKeepParent = keptValues.has(parent.value)
      const hasKeptChildren = keptChildren?.length

      if (shouldKeepParent || hasKeptChildren) {
        const updatedParent = { ...parent, ...(hasKeptChildren && { children: keptChildren }) }
        return updatedParent
      }
      return null
    })

    const filteredResult = mappedParents.filter((item) => !!item) as TreeSelectOption[]
    onChange(filteredResult)
  }

  const handleChipDelete = (event: MouseEvent, itemValue: string) => {
    event.stopPropagation()
    handleRemoveItem(itemValue)
  }

  if (!selectedItems.length) {
    return null
  }

  return (
    <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
      {/* First row items */}
      <Stack width="100%" direction="row" gap={1} flexWrap="nowrap" alignItems="center">
        {firstRowItems.map((item) => {
          const isItemInvalid = invalidItems?.has(item.value)
          return (
          <StyledChip
            key={item.value}
            label={
              <span className="chip-label">
                {item.type && <span className="chip-label-prefix">{item.type}:</span>}
                <span className="chip-label-content" style={isItemInvalid ? { marginRight: 0 } : undefined}>{item.label}</span>
                {isItemInvalid && (
                  <ExclamationCircleIcon
                    width={16}
                    height={16}
                    color={theme.colorTokens.info.attention}
                    style={{ flexShrink: 0, marginLeft: 8, marginRight: 12 }}
                  />
                )}
              </span>
            }
            className={`${firstRowItems.length > 1 ? 'clipped-chip' : ''}`}
            onDelete={() => handleRemoveItem(item.value)}
            deleteIcon={<CloseIcon />}
          />
        )})}
      </Stack>

      {/* Show third chip for 3+ items */}
      {secondRowChipItem && (() => {
        const isItemInvalid = invalidItems?.has(secondRowChipItem.value)
        return (
          <StyledChip
            key={secondRowChipItem.value}
            label={
              <span className="chip-label">
                <span className="chip-label-prefix">{secondRowChipItem.type}:</span>
                <span className="chip-label-content" style={isItemInvalid ? { marginRight: 0 } : undefined}>{secondRowChipItem.label}</span>
                {isItemInvalid && (
                  <ExclamationCircleIcon
                    width={16}
                    height={16}
                    color={theme.colorTokens.info.attention}
                    style={{ flexShrink: 0, marginLeft: 8, marginRight: 12 }}
                  />
                )}
              </span>
            }
            onDelete={() => handleRemoveItem(secondRowChipItem.value)}
            deleteIcon={<CloseIcon />}
          />
        )
      })()}

      {/* Remaining items in a select - only show if 4+ items */}
      {selectItems.length > 0 && (
        <StyledSelect
          multiple
          value={selectItems.map((item) => item.value)}
          onChange={handleRemainingItemRemove}
          renderValue={(selected) => {
            const count = (selected as string[]).length
            return `+${count}`
          }}
          MenuProps={DEFAULT_MENU_PROPS}
          IconComponent={KeyboardArrowDownIcon}
          displayEmpty
        >
          {selectItems.map((item) => {
            const isItemInvalid = invalidItems?.has(item.value)
            return (
              <MenuItem key={item.value} value={item.value}>
                <StyledChip
                  label={
                    <span className="chip-label">
                      {item.type && <span className="chip-label-prefix">{item.type}:</span>}
                      <span className="chip-label-content" style={isItemInvalid ? { marginRight: 0 } : undefined}>{item.label}</span>
                      {isItemInvalid && (
                        <ExclamationCircleIcon
                          width={16}
                          height={16}
                          color={theme.colorTokens.info.attention}
                          style={{ flexShrink: 0, marginLeft: 8, marginRight: 12 }}
                        />
                      )}
                    </span>
                  }
                  onDelete={(event) => handleChipDelete(event, item.value)}
                  deleteIcon={<CloseIcon />}
                  className="select-item-chip"
                />
              </MenuItem>
            )
          })}
        </StyledSelect>
      )}
    </Stack>
  )
}

export default SelectedItemsDisplay
